/**
 * Results Forward KPIs — Counter Animation
 *
 * Behaviour:
 * - Observes the custom element entering the viewport (IntersectionObserver).
 * - On first intersection, animates each [data-target] counter from 0 → target.
 * - Uses requestAnimationFrame + easeOutQuart for a premium feel.
 * - Fires exactly once per page load; observer disconnects after trigger.
 * - Prefix and suffix are static DOM spans — only the count span is updated,
 *   preventing layout shift during animation.
 */

if (!customElements.get('results-forward-kpis')) {
  customElements.define(
    'results-forward-kpis',
    class ResultsForwardKpis extends HTMLElement {
      constructor() {
        super();
        /** @type {IntersectionObserver|null} */
        this._observer = null;
        /** Ensures animation runs only once per page load */
        this._animated = false;
      }

      connectedCallback() {
        this._observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting && !this._animated) {
                this._animated = true;
                this._animateAll();
                // Disconnect so subsequent scroll re-entries do nothing
                this._observer.disconnect();
              }
            });
          },
          {
            // Trigger when 20% of the section is visible
            threshold: 0.2,
          }
        );

        this._observer.observe(this);
      }

      disconnectedCallback() {
        if (this._observer) {
          this._observer.disconnect();
          this._observer = null;
        }
      }

      /** Kick off a counter for every [data-target] element in this section */
      _animateAll() {
        const counters = this.querySelectorAll('[data-target]');

        counters.forEach((el) => {
          const target = parseInt(el.dataset.target, 10);

          if (isNaN(target)) return;

          this._animateCounter(el, target);
        });
      }

      /**
       * Animate a single counter element from 0 → target.
       *
       * @param {HTMLElement} el       - The span whose textContent we update.
       * @param {number}      target   - The final numeric value.
       * @param {number}      duration - Animation duration in ms (default 1600).
       */
      _animateCounter(el, target, duration = 1600) {
        const startTime = performance.now();

        const tick = (now) => {
          const elapsed = now - startTime;
          const progress = Math.min(elapsed / duration, 1);

          // easeOutQuart: fast start, graceful deceleration
          const eased = 1 - Math.pow(1 - progress, 4);
          const current = Math.round(eased * target);

          el.textContent = current;

          if (progress < 1) {
            requestAnimationFrame(tick);
          } else {
            // Guarantee exact final value
            el.textContent = target;
          }
        };

        requestAnimationFrame(tick);
      }
    }
  );
}
